import React, { useState, useEffect, Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import theme from './theme';

// Eagerly loaded: entry pages and layout shells used on (almost) every visit.
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Home from './components/Home';
import PublicSite from './components/PublicSite';
import { useTranslation } from 'react-i18next';
import axios, { stopSessionHealthCheck } from './utils/axiosConfig';
import { subscribeToEvents } from './utils/eventStream';
import { parsePublicPath, PUBLIC_PATHS } from './utils/publicLocale';

// Route-level code splitting: heavier / rarely-visited screens load on demand.
const Settings = lazy(() => import('./components/Settings'));
const ManageUsers = lazy(() => import('./components/ManageUsers'));
const ManageRoles = lazy(() => import('./components/ManageRoles'));
const ManagePermissions = lazy(() => import('./components/ManagePermissions'));
const Profile = lazy(() => import('./components/Profile'));
const NotificationSettings = lazy(() => import('./components/NotificationSettings'));
const SessionManagement = lazy(() => import('./components/SessionManagement'));
const AdminSettings = lazy(() => import('./components/AdminSettings'));
const Forums = lazy(() => import('./components/Forums'));
const FollowedThreads = lazy(() => import('./components/FollowedThreads'));
const MyThreadsPosts = lazy(() => import('./components/MyThreadsPosts'));
const Help = lazy(() => import('./components/Help'));
const ThreadsByTag = lazy(() => import('./components/ThreadsByTag'));
const Sliders = lazy(() => import('./components/Sliders'));
const ManagePublicContent = lazy(() => import('./components/ManagePublicContent'));
const ManageLocations = lazy(() => import('./components/ManageLocations'));
const Submissions = lazy(() => import('./components/Submissions'));
const Resources = lazy(() => import('./components/Resources'));
const Reminders = lazy(() => import('./components/Reminders'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const SecurityAlert = lazy(() => import('./components/SecurityAlert'));
const Gallery = lazy(() => import('./components/Gallery'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const NotFound = lazy(() => import('./components/NotFound'));

// Lightweight fallback shown while a lazy route chunk is being fetched.
function RouteLoadingFallback() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress />
        </Box>
    );
}

// Wrap lazy route elements nested inside the Dashboard layout so that the
// layout (nav, app bar) stays mounted while a route chunk is being fetched.
const withSuspense = (element) => (
    <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
);

// Old public routes (/about, /locations, ...) now live as anchor sections on
// the single-page public site. With HashRouter the URL hash belongs to the
// router, so we redirect to "/" and pass the target section id via location
// state; PublicSite scrolls to it after mount.
function RedirectToSection({ section }) {
    const navigate = useNavigate();
    useEffect(() => {
        navigate('/', { replace: true, state: { scrollTo: section } });
    }, [navigate, section]);
    return null;
}

// Hoisted OUT of App() on purpose: this used to be declared inside App's
// render body, which meant every App re-render created a brand-new
// AppRoutes *function* (a new component type as far as React is concerned).
// App itself subscribes to i18n via useTranslation(), so i18n.changeLanguage()
// (e.g. the public-page language switcher) re-rendered App on every call,
// which handed React a "new" AppRoutes type and forced a full unmount+remount
// of the entire route tree underneath — including whatever page was showing.
// That silently reset any of that page's local state on every language
// switch; on NotFound specifically it was visible as the switcher reverting
// itself a moment after every click (the remounted page re-ran its
// "seed language from the URL" effect from scratch). Keeping AppRoutes as a
// stable, module-level function fixes this for every page, not just 404.
function AppRoutes({ t, i18n, user, setUser, handleLogin, handleLogout }) {
    const location = useLocation();
    const titleMap = {
        '/login': t('login.title'),
        '/dashboard': t('dashboard.title'),
        '/dashboard/settings': t('settings.title'),
        '/dashboard/settings/profile': t('profile.title'),
        '/dashboard/settings/notifications': t('notificationSettings.title'),
        '/dashboard/settings/sessions': t('settings.sessions'),
        '/dashboard/settings/admin': t('settings.adminSettings'),
        '/dashboard/settings/users': t('settings.manageUsers'),
        '/dashboard/settings/roles': t('settings.manageRoles'),
        '/dashboard/settings/permissions': t('settings.managePermissions'),
        '/dashboard/forums': t('dashboard.forums'),
        '/dashboard/followed-threads': t('dashboard.followedThreads'),
        '/dashboard/my-threads-posts': t('dashboard.myThreadsPosts'),
        '/dashboard/help': t('dashboard.help'),
        '/dashboard/resources': t('dashboard.resources'),
        '/dashboard/gallery': t('dashboard.gallery'),
        '/dashboard/sliders': t('dashboard.sliders'),
        '/dashboard/site-content': t('cms.menu.siteContent'),
        '/dashboard/locations': t('cms.menu.locations'),
        '/dashboard/submissions': t('submissions.title'),
        '/dashboard/reminders': t('dashboard.reminders'),
    };
    useEffect(() => {
        // Public pages (/, /en, /privacy, …) own their SEO <title> via their
        // own components. Only set the title for the app/auth routes mapped
        // above, so we never clobber the marketing/SEO title.
        const mapped = titleMap[location.pathname];
        if (mapped) document.title = mapped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language, location.pathname, t]);

    // If a hash/app route is active but the document path still carries /en
    // (user came from the English marketing page), drop it — the app is
    // language-agnostic and driven by the user profile, not the URL.
    // Keep the document path in sync with the hash route so clean-path
    // segments never linger. The inner route lives ONLY in the hash; the
    // document path carries just the locale root:
    //   public page  -> "/" (French) or "/en" (English)
    //   private/app  -> always "/" (app language comes from the profile)
    // Without this, loading /privacy (mapped by the bootstrap to #/privacy)
    // left "/privacy" in the path, so later nav produced "/privacy/#/".
    // Guarded off during react-snap prerender so build output is unchanged.
    useEffect(() => {
        if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
        if (window.location.hash.length <= 1) return; // no hash route yet
        const hashRoute = window.location.hash
            .replace(/^#/, '').split('?')[0].replace(/\/+$/, '') || '/';
        const { lang } = parsePublicPath(window.location.pathname);
        const desiredPath = PUBLIC_PATHS.includes(hashRoute)
            ? (lang === 'en' ? '/en' : '/')
            : '/';
        const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
        if (currentPath !== desiredPath) {
            window.history.replaceState(null, '', desiredPath + window.location.hash);
        }
    }, [location]);

    const requireAuth = (element) => {
        if (user) return element;
        return <Navigate to="/login" state={{ from: location }} replace />;
    };

    return (
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
            <Route path="/" element={<PublicSite />} />
            <Route path="/privacy" element={withSuspense(<PrivacyPolicy />)} />
            <Route path="/404" element={withSuspense(<NotFound />)} />
            <Route path="/about" element={<RedirectToSection section="about" />} />
            <Route path="/locations" element={<RedirectToSection section="locations" />} />
            <Route path="/join-our-team" element={<RedirectToSection section="join-team" />} />
            <Route path="/opportunity" element={<RedirectToSection section="entrepreneurs" />} />
            <Route path="/contact" element={<RedirectToSection section="join-team" />} />
            <Route path="/login" element={user ? <Navigate to={location.state?.from?.pathname || "/dashboard"} replace /> : <Login onLogin={handleLogin} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/security-alert" element={<SecurityAlert />} />
            <Route path="/dashboard" element={requireAuth(<Dashboard user={user} onLogout={handleLogout} />)}>
                <Route index element={<Home user={user} />} />
                <Route path="settings" element={withSuspense(<Settings user={user} />)}>
                    <Route path="notifications" element={withSuspense(<NotificationSettings user={user} setUser={setUser} />)} />
                    <Route path="profile" element={withSuspense(<Profile user={user} />)} />
                    <Route path="sessions" element={withSuspense(<SessionManagement user={user} onLogout={handleLogout} />)} />
                    {user && user.role && user.role.name.toLowerCase() === 'admin' && (
                        <>
                            <Route path="admin" element={withSuspense(<AdminSettings />)} />
                            <Route path="users" element={withSuspense(<ManageUsers />)} />
                            <Route path="roles" element={withSuspense(<ManageRoles />)} />
                            <Route path="permissions" element={withSuspense(<ManagePermissions />)} />
                        </>
                    )}
                </Route>
                <Route path="forums" element={withSuspense(<Forums user={user} />)} />
                <Route path="forums/tag/:tag" element={withSuspense(<ThreadsByTag user={user} />)} />
                <Route path="forums/:forumId" element={withSuspense(<Forums user={user} />)} />
                <Route path="forums/:forumId/threads" element={withSuspense(<Forums user={user} />)} />
                <Route path="forums/:forumId/threads/:threadId" element={withSuspense(<Forums user={user} />)} />
                <Route path="followed-threads" element={withSuspense(<FollowedThreads user={user} />)} />
                <Route path="my-threads-posts" element={withSuspense(<MyThreadsPosts user={user} />)} />
                <Route path="help" element={withSuspense(<Help user={user} />)} />

                <Route path="gallery" element={withSuspense(<Gallery user={user} />)} />
                <Route path="sliders" element={withSuspense(<Sliders user={user} />)} />
                {user && user.role && (user.role.name.toLowerCase() === 'admin' || user.role.Permissions?.some(p => p.name === 'manage_submissions')) && (
                    <Route path="submissions" element={withSuspense(<Submissions />)} />
                )}
                {user && user.role && (user.role.name.toLowerCase() === 'admin' || user.role.Permissions?.some(p => p.name === 'manage_public_content')) && (
                    <>
                        <Route path="site-content" element={withSuspense(<ManagePublicContent />)} />
                        <Route path="locations" element={withSuspense(<ManageLocations />)} />
                    </>
                )}
                <Route path="resources" element={withSuspense(<Resources user={user} />)} />
                <Route path="reminders" element={withSuspense(<Reminders user={user} />)} />
                <Route path="reminders/:id" element={withSuspense(<Reminders user={user} />)} />
            </Route>
            {/* Catch-all: any unmatched hash route (broken/old deep link)
                renders the themed 404 instead of a blank screen. */}
            <Route path="*" element={withSuspense(<NotFound />)} />
        </Routes>
        </Suspense>
    );
}

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();

    useEffect(() => {
        // Check if user is logged in by trying to fetch profile.
        // Skip the request entirely when the non-httpOnly `isAuthed` hint
        // cookie is absent: no session could exist, so calling /profile would
        // only produce a 401 (and a console error) on the public page.
        const hasAuthHint = document.cookie
            .split('; ')
            .some((c) => c.startsWith('isAuthed='));

        const checkAuth = async () => {
            if (!hasAuthHint) {
                setUser(null);
                setLoading(false);
                return;
            }
            try {
                const response = await axios.get('/api/auth/profile');
                if (response.data) {
                    setUser(response.data);
                    if (response.data.language) {
                        i18n.changeLanguage(response.data.language);
                    }
                }
            } catch (error) {
                // Not authenticated or token expired
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for session termination events via the consolidated SSE stream
    // (one /api/events/stream connection shared by all components).
    useEffect(() => {
        if (!user) return; // Only listen when user is authenticated

        const unsubscribe = subscribeToEvents('sessions', (data) => {
            console.log('SSE Sessions: Received event:', data);
            if (data.event === 'terminated') {
                // Rebroadcast in the legacy shape for other components
                // (e.g. SessionManagement) that listen for 'session-sse'.
                window.dispatchEvent(new CustomEvent('session-sse', { detail: { type: 'terminated' } }));
                console.log('SSE Sessions: Session terminated, logging out...');
                // If the user is securing their own account, SecurityAlert
                // is about to hand them off to set a new password. Just drop
                // the auth state here; don't run the logout redirect that
                // would bounce them to /login and abort that hand-off.
                const hash = window.location.hash || '';
                const inSecureFlow = hash.startsWith('#/security-alert') || hash.startsWith('#/reset-password');
                if (inSecureFlow) {
                    stopSessionHealthCheck();
                    setUser(null);
                    return;
                }
                handleLogout();
            }
        });

        return () => {
            console.log('SSE Sessions: Cleaning up session stream listener');
            unsubscribe();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleLogin = (userData) => {
        setUser(userData);
        if (userData.language) {
            i18n.changeLanguage(userData.language);
        }
    };

    const handleLogout = async () => {
        try {
            await axios.post('/api/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            stopSessionHealthCheck();
            setUser(null);
            // Full reload to a clean slate: drops all React state, the shared
            // SSE stream, and any cached (e.g. admin all-sessions) data so
            // nothing from this account can bleed into the next login in the
            // same browser.
            window.location.href = '/#/login';
            window.location.reload();
        }
    };

    if (loading) {
        return null; // or a loading spinner
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <AppRoutes
                    t={t}
                    i18n={i18n}
                    user={user}
                    setUser={setUser}
                    handleLogin={handleLogin}
                    handleLogout={handleLogout}
                />
            </Router>
        </ThemeProvider>
    );
}

export default App;
